import React, { useEffect, useRef, useState } from "react";
import { Container, Stack } from "react-bootstrap";
import NavCrumbs from "../../../common/NavCrumbs";
import useBreadcrumbs from "../../../hooks/useBreadCrumbs";
import SearchInput from "../../../common/InlineSearch";
import { fetchSystemLocationList } from "../../../services/SystemLocationAPI";
import DataTable from "../../../common/DataTable";
import { SYSTEM_LOCATION_HEADERS } from "./actions/constant";
import CustomPagination from "../../../common/Pagination";
import TableRowsPerPage from "../../../common/TableRowsPerPage";
import AddUpdateMedicareLocation from "./components/AddUpdateMedicareLocation";

const SystemLocationList = () => {
  const breadcrumbs = useBreadcrumbs();
  const [openLocationDrawer, setOpenLocationDrawer] = useState<boolean>(false);
  const [action, setAction] = useState<string>("");
  const [selectedLocation, setSelectedLocation] = useState<any>(null);
  const [locationData, setLocationData] = useState<any[]>([]);
  const searchInputRef = useRef<any>(null);
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(15);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalRows, setTotalRows] = useState<any>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchQuerylength, setSearchQuerylength] = useState<string>("");

  const fetchSystemLocationData = async (
    query: string = "",
    pageNo: number = 1,
    pageSize: number = itemsPerPage
  ) => {
    const trimmedQuery = query?.trim();
    const filterParams = {
      searchString: trimmedQuery,
      pageNo,
      pageSize,
    };
    setLoading(true);
    try {
      const medicarelocationData = await fetchSystemLocationList(filterParams);
      if (medicarelocationData) {
        setData(medicarelocationData);
        setLocationData(medicarelocationData);
        setTotalPages(medicarelocationData[0]?.totalPages || 1);
        setTotalRows(medicarelocationData[0]?.totalCount || null);
      } else {
        console.error("No data found in medicarelocationData result");
      }
    } catch (error) {
      console.error("Error fetching medicarelocationData:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSystemLocationData(searchQuery, currentPage, itemsPerPage);
  }, [itemsPerPage, currentPage]);

  const handleSearchQueryChange = (query: string) => {
    setSearchQuery(query);
    fetchSystemLocationData(query, 1, itemsPerPage);
    setCurrentPage(1);
  };

  const handleSearchQueryChangeForlength = (query: string) => {
    setSearchQuerylength(query?.trim());
  };

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    fetchSystemLocationData(searchQuery, pageNumber, itemsPerPage);
  };

  // Update the number of rows displayed per page
  const handleRowsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleReset = () => {
    searchInputRef.current?.clear();
    setSearchQuery("");
    setSearchQuerylength("");
    fetchSystemLocationData();
  };

  const handleRowAction = (row: any, action: string) => {
    setAction(action);
    setOpenLocationDrawer(true);
    setSelectedLocation(row);
    setAction("Edit");
  };

  const handleMapAction = (row: any, action: string) => {
    const rowLocation = {
      fax: row?.faxNumber || "NA",
      phone: row?.telephoneNumber || "NA",
      email: row?.emailId || "NA",
      locationname: row?.providerName || "NA",
      locationaddress: [
        row?.providerAddress,
        row?.physicalCity,
        row?.physicalState,
        row?.physicalZip,
      ]
        ?.filter(Boolean)
        .join(", "),
      latitude: row?.latitude || null,
      longitude: row?.longitude || null,
    };
    sessionStorage.setItem(
      "selectedMapData",
      JSON.stringify({
        locations: [rowLocation],
        tab: "QME Schedule",
      })
    );

    window.open("/#/map/associated_locations", "_blank");
  };

  const handleAddLocation = () => {
    setAction("Add");
    setSelectedLocation(null);
    setOpenLocationDrawer(true);
  };

  const entriesText = !totalRows
    ? "Showing 0 of 0 entries"
    : `Showing ${(currentPage - 1) * itemsPerPage + 1} to ${Math.min(
        currentPage * itemsPerPage,
        totalRows
      )} of ${totalRows} entries`;

  const handleCancel = () => {
    setOpenLocationDrawer(false);
    setCurrentPage(1);
    handleReset();
  };

  return (
    <div>
      <div className="breadcrumbg">
        <Container fluid className="px-5">
          <Stack direction="horizontal" gap={2}>
            <div className="pt-0 flex-column d-flex align-items-start fade-in-left">
              <h4>Location</h4>
              <NavCrumbs breadcrumbs={breadcrumbs} />
            </div>
            <div className="p-2 ms-auto">
              {!openLocationDrawer ? (
                <button
                  onClick={handleAddLocation}
                  className="btn btn-primary d-flex align-items-center fade-in-right"
                >
                  <span className="material-icons me-2">add</span>
                  Location
                </button>
              ) : (
                ""
              )}
            </div>
          </Stack>
        </Container>
      </div>
      {!openLocationDrawer ? (
        <>
          <div className="main_wrapper_section">
            <div className="container-fluid px-5">
              <div className="d-flex   py-2 search_closer align-items-center">
                <div className="sectionTitle noborder me-auto">List</div>
                <SearchInput
                  ref={searchInputRef}
                  data={data}
                  searchKeys={[]}
                  onSearchResult={setLocationData}
                  placeholder="Search"
                  onSearchQueryChange={handleSearchQueryChange}
                  onInputChange={handleSearchQueryChangeForlength}
                />
                <button
                  onClick={handleReset}
                  className="reset_btn"
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "5px",
                  }}
                >
                  <span className="material-icons reset_ic0">restart_alt</span>
                </button>
              </div>
              <div className="borderB mb-3"></div>
              <div className="adjustHeightTb">
                <DataTable
                  headers={SYSTEM_LOCATION_HEADERS}
                  rows={locationData}
                  onAction={handleRowAction}
                  onDownloadAction={handleMapAction}
                  checkInstruction={true}
                />
              </div>
              <div className="d-flex justify-content-between align-items-center mt-0">
                {/* Display entries text on the left */}
                <div className="col-md-12">
                  <div className="row">
                    <div className="col-md-3 showingData_wrapper">
                      <span>{entriesText}</span>
                    </div>
                    <div className="col-md-9">
                      <div className="d-flex flex-row-reverse ">
                        <CustomPagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={paginate}
                        />
                        <TableRowsPerPage
                          value={itemsPerPage}
                          onSelect={handleRowsPerPageChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <AddUpdateMedicareLocation
          onCancel={handleCancel}
          selectedLocation={selectedLocation?.medicareId}
        />
      )}
    </div>
  );
};

export default SystemLocationList;
